import { useState, useCallback } from 'react';
import { Link } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Button, Logo, MonoLabel, Pill } from '../components/primitives/index.js';
import { PVDocPreview, TurnstileWidget } from '../components/composites/index.js';

const METRICS = [
  ['68%', 'rată de succes medie pe amenzi de viteză'],
  ['3 min', 'timp mediu de la upload la PDF gata'],
  ['49 lei', 'preț fix, fără surprize, doar la final'],
  ['127.4M', 'lei evitați de utilizatori în 2025'],
] as const;

const HOW = [
  ['01', 'Fotografiezi PV-ul', 'O singură poză. AI-ul extrage fapta, articolele, data și agentul.'],
  ['02', 'Răspunzi 3 întrebări', 'În română normală. Despre cum a decurs constatarea.'],
  ['03', 'AI găsește breșele', 'Verificăm 47 de criterii de nulitate și 8.300 spețe câștigate.'],
  ['04', 'Primești PDF-ul', 'Plângerea completă, citată, gata de semnat și depus.'],
] as const;

const FINE_TYPES = [
  ['Viteză & alcoolemie', '4.821', '76%'],
  ['Parcare & staționare', '3.207', '82%'],
  ['Trecere pe roșu', '1.840', '64%'],
  ['Ordine publică', '892', '71%'],
  ['Câine fără lesă, gunoi', '614', '88%'],
  ['ANAF & fiscale', '457', '58%'],
] as const;

const TESTIMONIALS = [
  {
    quote:
      'Mi-au luat 2.900 lei pentru „roșu", dar n-am trecut. Plângerea generată în 4 minute a fost atât de bine făcută încât procurorul nici n-a contestat. Anulată.',
    name: 'M.D.',
    loc: 'București',
    won: '2.900 lei',
  },
  {
    quote:
      'Aveam radarul agentului la 100m. AI-ul a observat că buletinul metrologic era expirat. Eu nu știam că am voie să-l verific. Acum scriem și plângerea soției.',
    name: 'C.R.',
    loc: 'Cluj-Napoca',
    won: '1.305 lei',
  },
  {
    quote:
      'Câinele meu nu era fără lesă, eu îi scoteam botnița să bea apă. Agentul nu a chemat martor. PV-ul a fost anulat la primul termen.',
    name: 'A.P.',
    loc: 'Iași',
    won: '500 lei',
  },
] as const;

function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const onToken = useCallback((t: string) => setToken(t), []);

  const join = useMutation({
    mutationFn: (e: string) =>
      api.joinWaitlist({ email: e, source: 'landing', turnstile_token: token ?? undefined }),
  });

  if (join.isSuccess) {
    return (
      <div className="bg-accent-soft border border-accent/30 rounded-lg px-5 py-4 text-sm text-fg-2 max-w-md">
        <strong className="text-accent-dark">Te-am adăugat pe listă.</strong> Îți scriem când dăm
        drumul la invitații, în câteva săptămâni.
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim() && token) join.mutate(email.trim());
      }}
      className="flex flex-col gap-3 max-w-md"
    >
      <div className="flex gap-2">
        <input
          type="email"
          required
          placeholder="adresa@email.ro"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-card border border-line rounded-full px-5 py-3 text-sm focus:outline-none focus:border-fg"
        />
        <button
          type="submit"
          disabled={join.isPending || !token}
          className="bg-fg text-bg rounded-full px-5 py-3 text-sm font-medium disabled:opacity-50"
        >
          {join.isPending ? '…' : 'Anunță-mă'}
        </button>
      </div>
      <TurnstileWidget onToken={onToken} />
      {join.isError && (
        <div className="text-xs text-danger">A apărut o problemă. Mai încearcă o dată.</div>
      )}
    </form>
  );
}

export function Landing() {
  return (
    <div className="bg-bg text-fg">
      {/* Nav */}
      <nav className="sticky top-0 z-10 px-5 md:px-10 lg:px-16 py-4 md:py-5 flex items-center justify-between border-b border-line bg-bg/85 backdrop-blur-xl">
        <Logo size="lg" />
        <div className="hidden lg:flex gap-9 text-sm text-muted">
          <a href="#cum">Cum funcționează</a>
          <a href="#amenzi">Tipuri de amenzi</a>
          <a href="#statistici">Statistici</a>
          <a href="#garantie">Garanție</a>
          <a href="#pret">Preț</a>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 text-sm">
          <Link href="/login" className="text-muted hidden sm:inline">
            Autentificare
          </Link>
          <Link
            href="/contest/new"
            className="bg-fg text-bg px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full font-medium whitespace-nowrap"
          >
            Începe gratuit
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-5 md:px-10 lg:px-16 pt-10 md:pt-20 pb-12 md:pb-16 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center max-w-[1440px] mx-auto">
        <div>
          <Pill className="mb-5 md:mb-7 bg-card border border-line text-muted" dot="oklch(0.72 0.18 145)">
            <span className="font-mono text-[11px]">14.382 contestații generate luna trecută</span>
          </Pill>
          <h1 className="text-[44px] leading-[1.02] sm:text-[56px] md:text-[72px] lg:text-display-1 font-medium text-pretty mb-5 md:mb-6 m-0">
            Amenda<br />se contestă<br />în{' '}
            <span className="text-accent italic font-serif font-normal">3 minute</span>.
          </h1>
          <p className="text-base md:text-xl text-muted leading-[1.5] max-w-[480px] mb-7 md:mb-9">
            AI-ul nostru citește procesul-verbal, găsește articolele de lege relevante și
            generează plângerea gata de depus la judecătorie. Tu doar semnezi.
          </p>
          <div className="flex flex-wrap gap-3 items-center mb-6">
            <Link href="/contest/new">
              <Button size="lg" pill>Încarcă procesul-verbal</Button>
            </Link>
            <div className="px-1 sm:px-4 py-1 sm:py-3 text-xs sm:text-sm text-muted">
              49 lei doar la final · garanție 30 zile
            </div>
          </div>
          <div className="mt-4">
            <MonoLabel className="mb-3">Lansăm cu invitație · înscrie-te în listă</MonoLabel>
            <WaitlistForm />
          </div>
        </div>

        {/* Visual stack — hidden on small screens (decorative, would overflow) */}
        <div className="relative h-[540px] hidden lg:flex items-center justify-center">
          <div className="absolute top-[30px] left-[60px] rotate-[-6deg]">
            <PVDocPreview scale={1.4} />
          </div>
          <div className="absolute top-[110px] right-0 w-[300px] bg-card border border-line rounded-2xl p-5 shadow-[0_24px_60px_rgba(0,0,0,0.10)]">
            <MonoLabel>ANALIZĂ AI · 2.4s</MonoLabel>
            <div className="mt-3.5">
              {[
                ['Articol 102 alin. (3)', '✓'],
                ['Lipsă martor asistent', '✓'],
                ['Eroare în descrierea faptei', '✓'],
              ].map(([t, c], i) => (
                <div
                  key={t}
                  className={[
                    'flex justify-between py-2.5 text-[13px]',
                    i ? 'border-t border-line' : '',
                  ].join(' ')}
                >
                  <span>{t}</span>
                  <span className="text-accent-dark font-semibold">{c}</span>
                </div>
              ))}
            </div>
            <div className="mt-3.5 p-3 bg-bg rounded-lg">
              <div className="mono-caps text-muted text-[11px] mb-1">Șanse estimate de succes</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[34px] font-semibold tracking-tight">78%</span>
                <span className="text-accent-dark text-xs">↑ peste medie</span>
              </div>
            </div>
          </div>
          <div className="absolute bottom-10 left-20 bg-fg text-bg px-4 py-3.5 rounded-xl text-[13px] flex items-center gap-2.5 shadow-[0_14px_40px_rgba(0,0,0,0.18)]">
            <span className="w-7 h-7 bg-accent rounded-full text-fg flex items-center justify-center font-bold">↓</span>
            Plangere_anuleaza.pdf
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section id="statistici" className="mx-5 md:mx-10 lg:mx-16 py-8 md:py-10 border-y border-line grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-[1440px] xl:mx-auto">
        {METRICS.map(([n, t]) => (
          <div key={t}>
            <div className="text-3xl sm:text-4xl lg:text-display-3 font-medium tracking-tight mb-2">{n}</div>
            <div className="text-[13px] text-muted leading-[1.45] max-w-[220px]">{t}</div>
          </div>
        ))}
      </section>

      {/* How */}
      <section id="cum" className="px-5 md:px-10 lg:px-16 pt-12 md:pt-20 pb-12 md:pb-14 max-w-[1440px] mx-auto">
        <MonoLabel>Cum funcționează</MonoLabel>
        <h2 className="text-3xl sm:text-4xl lg:text-display-3 font-medium mb-8 md:mb-10 mt-3 max-w-[760px]">
          De la foto la plângere depusă, fără să ridici telefonul.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HOW.map(([n, t, d]) => (
            <div key={n} className="bg-card p-5 sm:p-7 rounded-2xl border border-line">
              <div className="font-mono text-xs text-accent-dark mb-4 sm:mb-6 tracking-[1px]">{n}</div>
              <div className="text-xl font-medium tracking-tight mb-2.5">{t}</div>
              <div className="text-[13.5px] text-muted leading-[1.55]">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Fine types */}
      <section id="amenzi" className="px-5 md:px-10 lg:px-16 pb-12 md:pb-14 max-w-[1440px] mx-auto">
        <div className="bg-fg text-bg rounded-3xl p-6 sm:p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div>
            <MonoLabel color="oklch(0.72 0.18 145)">Tipuri de contravenții</MonoLabel>
            <h2 className="text-3xl sm:text-4xl lg:text-display-4 font-medium leading-tight lg:leading-none mt-3 mb-4 text-bg">
              Contestăm orice<br />amendă, nu doar de circulație.
            </h2>
            <p className="text-base text-[#A8B0A2] leading-[1.55] max-w-[420px]">
              De la depășire de viteză la câine fără lesă. Dacă există un proces-verbal, AI-ul îl
              analizează.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {FINE_TYPES.map(([cat, n, rate]) => (
              <div
                key={cat}
                className="p-4 rounded-xl border border-white/10"
                style={{ background: 'rgba(255,255,255,0.04)' }}
              >
                <div className="text-[13px] font-medium mb-1.5 text-bg">{cat}</div>
                <div className="flex justify-between font-mono text-[11px] text-[#A8B0A2]">
                  <span>{n} cazuri</span>
                  <span className="text-accent">{rate} succes</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-5 md:px-10 lg:px-16 py-12 md:py-14 max-w-[1440px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8 md:mb-10">
          <div>
            <MonoLabel>Oameni reali · dosare reale</MonoLabel>
            <h2 className="text-3xl sm:text-4xl lg:text-display-4 font-medium mt-3 max-w-[600px]">
              Au scăpat de amendă în{' '}
              <span className="text-accent-dark italic font-serif font-normal">câteva minute</span>.
            </h2>
          </div>
          <div className="text-[13px] text-muted font-mono hidden sm:block">VEZI TOATE 8.347 →</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.name + t.won}
              className="bg-card p-6 rounded-2xl border border-line flex flex-col"
            >
              <div className="text-[15px] leading-[1.55] text-fg-2 mb-6 flex-1 text-pretty">
                „{t.quote}"
              </div>
              <div className="pt-4 border-t border-line flex justify-between items-center">
                <div>
                  <div className="text-[13px] font-medium">{t.name}</div>
                  <div className="text-[11px] text-muted">{t.loc}</div>
                </div>
                <Pill bg="oklch(0.94 0.06 145)" color="oklch(0.52 0.18 145)" dot="oklch(0.72 0.18 145)">
                  <span className="font-mono text-[11px]">+{t.won}</span>
                </Pill>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pret" className="px-5 md:px-10 lg:px-16 py-12 md:py-14 pb-16 md:pb-20 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <MonoLabel>Preț transparent</MonoLabel>
            <h2 className="text-4xl sm:text-5xl lg:text-display-2 font-medium mt-3 mb-4">
              <span className="text-accent">49 lei.</span>
              <br />
              Atât.
            </h2>
            <p className="text-base text-muted leading-[1.55] max-w-[460px] mb-6">
              Un preț, plătit doar la final. Fără abonament, fără surprize. Dacă pierdem în primă
              instanță, primești banii înapoi.
            </p>
            <ul className="space-y-2 text-sm text-fg-2">
              {[
                'Plângere PDF gata de printat',
                'Revizie avocat partener',
                '8-12 spețe similare atașate',
                'Tracking dosar până la sentință',
                'Garanție 30 zile',
              ].map((l) => (
                <li key={l} className="flex items-center gap-2.5">
                  <span className="w-4 h-4 bg-accent rounded-full flex items-center justify-center text-[10px] text-fg font-bold">
                    ✓
                  </span>
                  {l}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-bg-2 rounded-3xl p-6 sm:p-10 relative overflow-hidden">
            <div
              className="absolute -top-10 -right-10 w-52 h-52 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, oklch(0.72 0.18 145 / 0.13) 0%, transparent 70%)',
              }}
            />
            <MonoLabel>Exemplu concret</MonoLabel>
            <div className="mt-4 space-y-3">
              <div className="flex justify-between py-3 border-b border-line">
                <span className="text-sm text-muted">Amenda inițială</span>
                <span className="font-mono">1.305 lei</span>
              </div>
              <div className="flex justify-between py-3 border-b border-line">
                <span className="text-sm text-muted">Cost anuleaza.ro</span>
                <span className="font-mono">49 lei</span>
              </div>
              <div className="flex justify-between py-3 border-b border-line">
                <span className="text-sm text-muted">Economie netă</span>
                <span className="font-mono font-semibold text-accent-dark">1.256 lei</span>
              </div>
              <div className="flex justify-between py-3 text-base font-medium">
                <span>Multiplicator</span>
                <span className="text-accent-dark">26× costul</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 md:px-10 lg:px-16 py-8 md:py-10 border-t border-line">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 max-w-[1440px] mx-auto">
          <div className="text-sm">
            anuleaza<span className="text-accent">.</span>ro · © {new Date().getFullYear()}
          </div>
          <div className="flex flex-wrap gap-4 sm:gap-6 text-sm text-muted">
            <Link href="/privacy">Confidențialitate</Link>
            <Link href="/terms">Termeni</Link>
            <a href="mailto:ajutor@anuleaza.ro">ajutor@anuleaza.ro</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
